"use client";

import { Mic, Square, X } from "lucide-react";
import type { Ref } from "react";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

export type DictationState =
  | "idle"
  | "requesting"
  | "recording"
  | "transcribing";

function dictationLabel(state: DictationState) {
  return state === "recording"
    ? "Stop voice input"
    : state === "requesting"
      ? "Waiting for microphone permission"
      : state === "transcribing"
        ? "Transcribing voice input"
        : "Start voice input";
}

/**
 * The composer's voice control as markup: the microphone, and while busy the
 * status, stop and cancel controls. PromptDictationButton owns recording,
 * transcription and the hold-to-talk shortcut and renders through this; on
 * its own the control records nothing.
 */
export function PromptDictationControl(props: {
  containerRef?: Ref<HTMLDivElement>;
  state: DictationState;
  /** Seconds recorded so far. */
  elapsed: number;
  blocked: boolean;
  onToggle?: () => void;
  onCancel?: () => void;
}) {
  const { state, elapsed } = props;
  const busy = state !== "idle";
  const label = dictationLabel(state);
  return (
    <div ref={props.containerRef} className="flex shrink-0 items-center gap-1">
      {busy && (
        <span
          className="px-1 text-xs tabular-nums text-muted-foreground"
          role="status"
        >
          {state === "recording"
            ? `Listening ${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, "0")}`
            : state === "requesting"
              ? "Microphone…"
              : "Transcribing…"}
        </span>
      )}
      <Button
        type="button"
        size="icon"
        variant="ghost"
        disabled={props.blocked}
        onClick={props.onToggle}
        className={cn(
          "size-8 rounded-md",
          !busy && "text-muted-foreground hover:text-foreground",
          busy && "mlx-prompt-dictation-button",
        )}
        data-dictation-state={state === "idle" ? undefined : state}
        aria-pressed={state === "recording"}
        aria-label={label}
        title={
          state === "idle"
            ? "Start voice input · Hold Option (⌥) to talk"
            : state === "recording"
              ? "Stop voice input · Enter to transcribe and send"
              : label
        }
        aria-keyshortcuts="Alt"
      >
        {state === "transcribing" || state === "requesting" ? (
          <Spinner aria-hidden />
        ) : state === "recording" ? (
          <Square className="size-3.5" aria-hidden />
        ) : (
          <Mic className="size-4" aria-hidden />
        )}
      </Button>
      {busy && (
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="size-8 text-muted-foreground"
          onClick={props.onCancel}
          aria-label="Cancel voice input"
          title="Cancel voice input"
        >
          <X className="size-4" aria-hidden />
        </Button>
      )}
    </div>
  );
}
